import React, { memo, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Stack, Button, Icon, TextField, Autocomplete, Typography, Card, Checkbox, Divider, InputAdornment, IconButton } from 'investira.react.components';
import { BaseTemplate } from '../../../components/templates';
import withMessage from '../../../hoc/withMessage';
import services from '../../../services';
import { ATIVO_TIPO } from '@investirapri/financa.js/lib/enums';
import utils from '../../../utils';
const ATIVO_TIPO_FILTERED = utils.helpers.sortAndFilterValues(ATIVO_TIPO, 'descricao', ['POU', 'CNTC', 'CNTI', 'CNTP', 'CARC', 'CARP', 'CARB', 'CARE', 'CARV', 'IMV', 'VEI', 'BEM', 'MOE', 'CRPS', 'CRPC', 'TOKEN', 'NFT', 'MTL', 'PDR', 'ART', 'EFCV', 'EFBD', 'EFCD', 'EFFA', 'EFSA', 'EFPG', 'RPPS', 'PBGL', 'VBGL', 'INSS', 'FGTS', 'EMPCT', 'EMPETF', 'EMPACN', 'EMPBRD', 'EMPCRPC', 'X', 'XDRI', 'XTBA', 'XTPU', 'XTPR', 'XACN', 'WA', 'CBLCX', 'CBLPR', 'OUR', 'IMOV', 'EMP']);
const AvaliacoesSettingsModeloEdit = memo(props => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const xModeloState = location.state || {};
  const xModeloId = Number(params.id || xModeloState.id || xModeloState.avaliacao_modelo_id);
  const [modelo, setModelo] = useState(xModeloState);
  const [sessoesQuestoes, setSessoesQuestoes] = useState([]);
  const [buscaQuestao, setBuscaQuestao] = useState('');
  const [questoesSelecionadas, setQuestoesSelecionadas] = useState({});
  const [isQuestoesModeloInitialized, setIsQuestoesModeloInitialized] = useState(false);
  const [questoesModeloOriginais, setQuestoesModeloOriginais] = useState([]);
  const xAtivoTipoOptions = useMemo(() => Object.values(ATIVO_TIPO_FILTERED || {}), []);
  useEffect(() => {
    if (!xModeloId) {
      return;
    }
    services.modelos.read(xModeloId, rRes => {
      setModelo(pModelo => ({
        ...pModelo,
        ...(rRes?.data || {})
      }));
    }, () => {
      props.onMessageError?.('Não foi possível carregar os dados do modelo.');
    });
  }, [props.onMessageError, xModeloId]);
  useEffect(() => {
    services.secoes.list({
      page: 1,
      size: 100,
      sort: 'avaliacao_secao_id asc'
    }, rSecoesRes => {
      const xSecoes = Object.values(rSecoesRes?.data || {}).map(xSecao => ({
        sessao_id: xSecao?.sessao_id ?? xSecao?.avaliacao_secao_id,
        sessao: xSecao?.sessao ?? xSecao?.avaliacao_secao
      })).filter(xSecao => xSecao.sessao_id && xSecao.sessao);
      services.questoes.list({
        page: 1,
        size: 500,
        sort: 'avaliacao_questao_id asc'
      }, rQuestoesRes => {
        const xQuestoes = Object.values(rQuestoesRes?.data || {}).map(xQuestao => ({
          id: xQuestao?.id ?? xQuestao?.avaliacao_questao_id,
          questao: xQuestao?.questao ?? xQuestao?.avaliacao_questao,
          sessao_id: xQuestao?.sessao_id ?? xQuestao?.avaliacao_secao_id
        })).filter(xQuestao => xQuestao.id && xQuestao.questao && xQuestao.sessao_id);
        const xSessoesQuestoesFromService = xSecoes.map(xSecao => ({
          ...xSecao,
          questoes: xQuestoes.filter(xQuestao => xQuestao.sessao_id === xSecao.sessao_id)
        })).filter(xSessao => xSessao.questoes.length > 0);
        setSessoesQuestoes(xSessoesQuestoesFromService);
      }, () => {
        props.onMessageError?.('Não foi possível carregar questões.');
        setSessoesQuestoes([]);
      });
    }, () => {
      props.onMessageError?.('Não foi possível carregar seções.');
      setSessoesQuestoes([]);
    });
  }, [props.onMessageError]);
  useEffect(() => {
    if (!xModeloId || isQuestoesModeloInitialized || !sessoesQuestoes.length) {
      return;
    }
    services.modelosQuestoes.list({
      avaliacao_modelo_id: xModeloId
    }, rRes => {
      const xQuestoesMap = sessoesQuestoes.flatMap(xSessao => xSessao.questoes || []).reduce((pAcc, pQuestao) => {
        pAcc[pQuestao.id] = pQuestao;
        return pAcc;
      }, {});
      const xQuestoesVinculadas = Object.values(rRes?.data || {}).map(xItem => Number(xItem?.avaliacao_questao_id ?? xItem?.questao_id ?? xItem?.id)).filter(Boolean);
      setQuestoesModeloOriginais(xQuestoesVinculadas);
      const xSelecionadasInicial = xQuestoesVinculadas.reduce((pAcc, pQuestaoId) => {
        const xQuestao = xQuestoesMap[pQuestaoId];
        if (!xQuestao) {
          return pAcc;
        }
        pAcc[pQuestaoId] = {
          id: xQuestao.id,
          questao: xQuestao.questao,
          sessao_id: xQuestao.sessao_id,
          peso: null
        };
        return pAcc;
      }, {});
      setQuestoesSelecionadas(xSelecionadasInicial);
      setIsQuestoesModeloInitialized(true);
    }, () => {
      props.onMessageError?.('Não foi possível carregar questões vinculadas ao modelo.');
      setIsQuestoesModeloInitialized(true);
    });
  }, [isQuestoesModeloInitialized, props.onMessageError, sessoesQuestoes, xModeloId]);
  const VALIDATION_SCHEMA = Yup.object().shape({
    titulo: Yup.string().trim().required('Informe o título do modelo.'),
    descricao: Yup.string().trim().nullable(),
    ativoTipoId: Yup.array().of(Yup.string()).nullable()
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      titulo: modelo?.avaliacao_modelo || modelo?.titulo || '',
      descricao: modelo?.descricao || '',
      ativoTipoId: Array.isArray(modelo?.ativo_tipo_id) ? modelo.ativo_tipo_id : []
    },
    validationSchema: VALIDATION_SCHEMA,
    validateOnMount: false,
    onSubmit: pValues => {
      const xQuestoesSelecionadas = Object.values(questoesSelecionadas).sort((a, b) => a.id - b.id);
      const xQuestoesSelecionadasIds = xQuestoesSelecionadas.map(xQuestao => Number(xQuestao.id));
      if (xTotalSelecionadas === 0) {
        props.onMessageError?.('Selecione ao menos uma questão para o modelo.');
        return;
      }
      if (!xModeloId) {
        props.onMessageError?.('Não foi possível identificar o modelo para edição.');
        return;
      }
      const xPayload = {
        id: xModeloId,
        avaliacao_modelo_id: xModeloId,
        avaliacao_modelo: pValues.titulo.trim(),
        descricao: pValues.descricao.trim(),
        ativo_tipo_id: pValues.ativoTipoId.length ? pValues.ativoTipoId : null,
        vigencia_inicio: modelo?.vigencia_inicio ? dayjs(modelo.vigencia_inicio).format('YYYY-MM-DD') : null,
        vigencia_fim: modelo?.vigencia_fim ? dayjs(modelo.vigencia_fim).format('YYYY-MM-DD') : null,
        qtd_questoes: xTotalSelecionadas
      };
      services.modelos.modify(xPayload, () => {
        const xIdsParaAdicionar = xQuestoesSelecionadasIds.filter(xId => !questoesModeloOriginais.includes(xId));
        const xIdsParaRemover = questoesModeloOriginais.filter(xId => !xQuestoesSelecionadasIds.includes(xId));
        Promise.all([...xIdsParaAdicionar.map(xQuestaoId => {
          return new Promise((resolve, reject) => {
            services.modelosQuestoes.add({
              avaliacao_modelo_id: Number(xModeloId),
              avaliacao_questao_id: Number(xQuestaoId)
            }, resolve, reject);
          });
        }), ...xIdsParaRemover.map(xQuestaoId => {
          return new Promise((resolve, reject) => {
            services.modelosQuestoes.remove({
              avaliacao_modelo_id: Number(xModeloId),
              avaliacao_questao_id: Number(xQuestaoId)
            }, resolve, reject);
          });
        })]).then(() => {
          setQuestoesModeloOriginais(xQuestoesSelecionadasIds);
          props.onMessageSuccess?.('Modelo atualizado com sucesso.');
          navigate('/avaliacoes/configuracoes/modelos');
        }).catch(rErr => {
          props.onMessageError?.(rErr?.message || 'Modelo atualizado, mas não foi possível vincular as questões.');
        });
      }, rErr => {
        props.onMessageError?.(rErr?.message || 'Não foi possível atualizar o modelo.');
      });
    }
  });
  const xTotalQuestoes = useMemo(() => {
    return sessoesQuestoes.reduce((pAcumulado, pSessao) => pAcumulado + pSessao.questoes.length, 0);
  }, [sessoesQuestoes]);
  const xSessoesFiltradas = useMemo(() => {
    const xBuscaNormalizada = buscaQuestao.trim().toLowerCase();
    return sessoesQuestoes.map(xSessao => {
      const xQuestoesFiltradas = xSessao.questoes.filter(xQuestao => {
        if (!xBuscaNormalizada) {
          return true;
        }
        return xQuestao.questao.toLowerCase().includes(xBuscaNormalizada);
      });
      return {
        ...xSessao,
        questoesFiltradas: xQuestoesFiltradas
      };
    }).filter(xSessao => xSessao.questoesFiltradas.length > 0);
  }, [buscaQuestao, sessoesQuestoes]);
  const xTotalSelecionadas = Object.keys(questoesSelecionadas).length;
  const isQuestaoSelecionada = pQuestaoId => {
    return Boolean(questoesSelecionadas[pQuestaoId]);
  };
  const handleToggleQuestao = pQuestao => {
    setQuestoesSelecionadas(pCurrent => {
      const xIsSelected = Boolean(pCurrent[pQuestao.id]);
      if (xIsSelected) {
        const xNewValue = {
          ...pCurrent
        };
        delete xNewValue[pQuestao.id];
        return xNewValue;
      }
      return {
        ...pCurrent,
        [pQuestao.id]: {
          id: pQuestao.id,
          questao: pQuestao.questao,
          sessao_id: pQuestao.sessao_id,
          peso: null
        }
      };
    });
  };
  const handleToggleSessao = pSessao => {
    setQuestoesSelecionadas(pCurrent => {
      const xIdsFiltrados = pSessao.questoesFiltradas.map(xQuestao => xQuestao.id);
      const xAllSelected = xIdsFiltrados.every(xId => Boolean(pCurrent[xId]));
      if (xAllSelected) {
        const xNewValue = {
          ...pCurrent
        };
        xIdsFiltrados.forEach(xId => {
          delete xNewValue[xId];
        });
        return xNewValue;
      }
      const xNewValue = {
        ...pCurrent
      };
      pSessao.questoesFiltradas.forEach(xQuestao => {
        if (!xNewValue[xQuestao.id]) {
          xNewValue[xQuestao.id] = {
            id: xQuestao.id,
            questao: xQuestao.questao,
            sessao_id: xQuestao.sessao_id,
            peso: null
          };
        }
      });
      return xNewValue;
    });
  };
  const handleSelectTodasFiltradas = () => {
    setQuestoesSelecionadas(pCurrent => {
      const xQuestoesFiltradas = xSessoesFiltradas.flatMap(xSessao => xSessao.questoesFiltradas);
      const xAllSelected = xQuestoesFiltradas.length > 0 && xQuestoesFiltradas.every(xQuestao => Boolean(pCurrent[xQuestao.id]));
      if (xAllSelected) {
        const xNewValue = {
          ...pCurrent
        };
        xQuestoesFiltradas.forEach(xQuestao => {
          delete xNewValue[xQuestao.id];
        });
        return xNewValue;
      }
      const xNewValue = {
        ...pCurrent
      };
      xQuestoesFiltradas.forEach(xQuestao => {
        if (!xNewValue[xQuestao.id]) {
          xNewValue[xQuestao.id] = {
            id: xQuestao.id,
            questao: xQuestao.questao,
            sessao_id: xQuestao.sessao_id,
            peso: null
          };
        }
      });
      return xNewValue;
    });
  };
  const handleScrollToSessao = pSessaoId => {
    const xElement = document.getElementById(`sessao-${pSessaoId}`);
    if (xElement) {
      xElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const handleSave = () => formik.submitForm();
  return <BaseTemplate title={'Editar Modelo'} subtitle={'Configurações'} description="Utilize modelos para padronizar critérios e estruturar avaliações personalizadas de acordo com as necessidades da sua estratégia. Esses modelos serão a base para organizar as questões e atribuir pesos, facilitando a análise dos resultados." maxWidth={'lg'} headerRenderActions={<Stack direction="row" spacing={2}>
          <Button disabled={props.disabledButtons} variant="contained" color="primary" startIcon={<Icon iconName="upload" size={16} />} size="small" sx={{
      px: 2
    }} onClick={handleSave}>
            Salvar
          </Button>
        </Stack>}>
      <Stack gap={2} py={3}>
        <TextField label="Título" placeholder="Ex.: Modelo Conservador" value={formik.values.titulo} onChange={formik.handleChange('titulo')} onBlur={formik.handleBlur('titulo')} error={formik.touched.titulo && Boolean(formik.errors.titulo)} helperText={formik.touched.titulo ? formik.errors.titulo : ''} fullWidth InputLabelProps={{
        shrink: true
      }} variant="outlined" />

        <TextField label="Descrição" placeholder="Descreva o objetivo deste modelo" value={formik.values.descricao} onChange={formik.handleChange('descricao')} onBlur={formik.handleBlur('descricao')} error={formik.touched.descricao && Boolean(formik.errors.descricao)} helperText={formik.touched.descricao ? formik.errors.descricao : ''} fullWidth InputLabelProps={{
        shrink: true
      }} variant="outlined" multiline rows={4} />

        <Stack>
          <Autocomplete multiple disablePortal options={xAtivoTipoOptions} value={xAtivoTipoOptions.filter(xAtivoTipo => (formik.values.ativoTipoId || []).includes(xAtivoTipo.id))} onChange={(_, pValues) => formik.setFieldValue('ativoTipoId', (pValues || []).map(xAtivoTipo => xAtivoTipo.id))} onBlur={() => formik.setFieldTouched('ativoTipoId', true)} getOptionLabel={pOption => pOption ? `${pOption.sigla} - ${pOption.descricao}` : ''} isOptionEqualToValue={(pOption, pValue) => pOption.id === pValue.id} renderInput={pParams => <TextField {...pParams} label="Tipo de Ativo" placeholder="Selecione" variant="outlined" error={Boolean(formik.touched.ativoTipoId && formik.errors.ativoTipoId)} />} />
          <Typography variant="caption" color="error.main" sx={{
          minHeight: 20,
          mt: 0.5
        }}>
            {formik.touched.ativoTipoId && formik.errors.ativoTipoId ? formik.errors.ativoTipoId : ' '}
          </Typography>
        </Stack>
      </Stack>

      <Divider />

      <Stack direction={{
      xs: 'column',
      lg: 'row'
    }} gap={3} py={3}>
        <Stack flexGrow={1} gap={2}>
          <Stack direction={{
          xs: 'column',
          md: 'row'
        }} gap={1.5}>
            <TextField label="Buscar questão" placeholder="Busque por palavra-chave no enunciado" value={buscaQuestao} onChange={pEvent => setBuscaQuestao(pEvent.target.value)} fullWidth InputLabelProps={{
            shrink: true
          }} variant="outlined" InputProps={{
            startAdornment: <InputAdornment position="start">
                    <Icon iconName="find" size={21} color={'secondaryLightness'} />
                  </InputAdornment>,
            endAdornment: <InputAdornment position="end">
                    <IconButton size={'small'} onClick={() => setBuscaQuestao('')} disabled={!buscaQuestao.trim()} color={'primary'}>
                      <Icon iconName={'cancel'} size={18} />
                    </IconButton>
                  </InputAdornment>
          }} />
            <Button variant="outlined" size="small" onClick={handleSelectTodasFiltradas} sx={{
            minWidth: {
              md: 220
            }
          }}>
              {xSessoesFiltradas.length > 0 && xSessoesFiltradas.flatMap(xSessao => xSessao.questoesFiltradas).every(xQuestao => isQuestaoSelecionada(xQuestao.id)) ? 'Limpar filtradas' : 'Selecionar filtradas'}
            </Button>
          </Stack>

          {xSessoesFiltradas.length === 0 && <Card>
              <Stack py={3} px={2}>
                <Typography variant="body2" color="textSecondary">
                  Nenhuma questão encontrada para o filtro aplicado.
                </Typography>
              </Stack>
            </Card>}

          {xSessoesFiltradas.map(xSessao => {
          const xTotalSessao = xSessao.questoesFiltradas.length;
          const xTotalSelecionadasSessao = xSessao.questoesFiltradas.filter(xQuestao => isQuestaoSelecionada(xQuestao.id)).length;
          const xAllSelected = xTotalSessao > 0 && xTotalSelecionadasSessao === xTotalSessao;
          return <Card key={xSessao.sessao_id} id={`sessao-${xSessao.sessao_id}`} sx={{
            scrollMarginTop: 88
          }}>
                <Stack p={2} gap={2}>
                  <Stack direction={{
                xs: 'column',
                md: 'row'
              }} alignItems={{
                xs: 'flex-start',
                md: 'center'
              }} justifyContent="space-between" gap={1}>
                    <Stack>
                      <Typography variant="h6" color="textPrimary">
                        {xSessao.sessao}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {xTotalSelecionadasSessao} de {xTotalSessao} selecionadas
                      </Typography>
                    </Stack>

                    <Button variant="text" size="small" onClick={() => handleToggleSessao(xSessao)}>
                      {xAllSelected ? 'Limpar sessão' : 'Selecionar sessão'}
                    </Button>
                  </Stack>

                  <Divider />

                  <Stack gap={1.5}>
                    {xSessao.questoesFiltradas.map(xQuestao => {
                  const xSelecionada = isQuestaoSelecionada(xQuestao.id);
                  return <Stack key={xQuestao.id} gap={1} sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 3
                  }}>
                          <Stack direction="row" gap={1} alignItems="center" flexGrow={1}>
                            <Checkbox checked={xSelecionada} onChange={() => handleToggleQuestao(xQuestao)} size="small" sx={{
                        mt: 0.2
                      }} />
                            <Stack direction="row" justifyContent="space-between" flexGrow={1}>
                              <Typography color="textPrimary" variant="body2">
                                {xQuestao.questao}
                              </Typography>
                              <Typography color="textSecondary" variant="caption">
                                ID #{xQuestao.id}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>;
                })}
                  </Stack>
                </Stack>
              </Card>;
        })}
        </Stack>

        <Stack minWidth={{
        lg: 280
      }} maxWidth={{
        lg: 320
      }} width="100%" gap={2} sx={{
        position: {
          lg: 'sticky'
        },
        top: {
          lg: 16
        },
        alignSelf: {
          lg: 'flex-start'
        }
      }}>
          <Card>
            <Stack p={2} gap={1}>
              <Typography variant="caption" color="textSecondary" sx={{
              textTransform: 'uppercase'
            }}>
                Sessões
              </Typography>

              {xSessoesFiltradas.map(xSessao => {
              // const xTotalSessao = xSessao.questoesFiltradas.length;
              // const xSelecionadasSessao = xSessao.questoesFiltradas.filter(xQuestao =>
              //   isQuestaoSelecionada(xQuestao.id)
              // ).length;

              return <Button key={xSessao.sessao_id} variant="text" size="small" onClick={() => handleScrollToSessao(xSessao.sessao_id)} sx={{
                justifyContent: 'start',
                px: 1,
                textTransform: 'none'
              }}>
                    <Stack direction="row" alignItems="center" width="100%" justifyContent="flex-start">
                      <Typography variant="body2" color="primary" sx={{
                    textAlign: 'left',
                    flexGrow: 1
                  }}>
                        {xSessao.sessao}
                      </Typography>
                      {/* <Typography variant="caption" color="textSecondary">
                        {xSelecionadasSessao}/{xTotalSessao}
                       </Typography> */}
                    </Stack>
                  </Button>;
            })}
            </Stack>
          </Card>

          <Card>
            <Stack p={2} gap={1}>
              <Typography variant="caption" color="textSecondary" sx={{
              textTransform: 'uppercase'
            }}>
                Resumo do modelo
              </Typography>
              <Typography variant="body2" color="textPrimary">
                Questões selecionadas: {xTotalSelecionadas}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total disponível: {xTotalQuestoes}
              </Typography>
            </Stack>
          </Card>
          <Stack spacing={2}>
            <Button disabled={props.disabledButtons} variant="contained" color="primary" startIcon={<Icon iconName="upload" size={16} />} size="small" sx={{
            px: 2
          }} onClick={handleSave}>
              Salvar
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </BaseTemplate>;
});
AvaliacoesSettingsModeloEdit.displayName = 'AvaliacoesSettingsModeloEdit';
export default withMessage(AvaliacoesSettingsModeloEdit);
